/**
 * quest-tracker-store — R232q22 Phase 3 Sirus rebuild.
 *
 * Lightweight Zustand store mirroring which quest IDs the player has
 * "tracked" (pinned to the on-screen QuestTracker sidebar). Persists
 * to localStorage `aiwari-quest-tracker` so a refresh keeps the same
 * pinned set.
 *
 * Capacity: WoW Sirus parity caps the tracker at 10 active quests; the
 * store enforces that limit by silently dropping new tracks once full
 * (caller is expected to surface a UI hint if desired).
 */

import { create } from 'zustand';

const STORAGE_KEY = 'aiwari-quest-tracker';
const MAX_TRACKED = 10;

interface QuestTrackerStore {
  tracked: Set<string>;
  track: (questId: string) => void;
  untrack: (questId: string) => void;
  isTracked: (questId: string) => boolean;
}

function load(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x): x is string => typeof x === 'string').slice(0, MAX_TRACKED));
  } catch {
    return new Set();
  }
}

function save(set: Set<string>) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...set])); } catch {}
}

export const useQuestTracker = create<QuestTrackerStore>((set, get) => ({
  tracked: load(),
  track: (questId: string) => {
    const cur = get().tracked;
    if (cur.has(questId) || cur.size >= MAX_TRACKED) return;
    const next = new Set(cur);
    next.add(questId);
    save(next);
    set({ tracked: next });
  },
  untrack: (questId: string) => {
    const cur = get().tracked;
    if (!cur.has(questId)) return;
    const next = new Set(cur);
    next.delete(questId);
    save(next);
    set({ tracked: next });
  },
  isTracked: (questId: string) => get().tracked.has(questId),
}));

export const QUEST_TRACKER_MAX = MAX_TRACKED;
